import { sendNotification, useTranslation } from '@msdk/common'
import { Card, CardHeader, styled, uiTokens } from '@msdk/ui'
import { MonitorOutlined, PersonOutlined } from '@mui/icons-material'
import { ComponentType } from 'react'
import { useWizard } from 'react-use-wizard'

import { useCodeContext } from './CodeContext'
import { FooterPopup } from './FooterPopup'
import { HeaderPopup } from './HeaderPopup'
import { ModalStepProps, StyledContent, StyledMessageTypography } from './shared'

type OptionDefinition = {
  icon: ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }>
  title: string
  description: string
  onClick: () => void
}

const StyledOptionCard = styled(Card)`
  &.MuiPaper-root {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${uiTokens.spacing.gap.small}px;
    width: 100%;
    box-sizing: border-box;
    padding: ${uiTokens.spacing.padding.medium}px;
    border-radius: ${uiTokens.radius.large}px;
    border: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
    background: ${uiTokens.uiControl.outlined.enabled};
    box-shadow: ${uiTokens.elevation.x[0]} ${uiTokens.elevation.y[100]}px ${uiTokens.elevation.blur[100]}px
      ${uiTokens.elevation.spread[100]} ${uiTokens.uiSurface.effect.shadow};
    cursor: pointer;
  }
`

const StyledOptionBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${uiTokens.spacing.gap.medium}px;
  width: 100%;
`

const StyledOptionTitle = styled(CardHeader)`
  padding: 0;
  .MuiCardHeader-title {
    color: ${uiTokens.uiContent.accentIntense};
  }
`

const StyledOptionIconWrap = styled('div')`
  align-items: center;
  width: 32px;
  height: 32px;
  color: ${uiTokens.uiControl.contained.enabled};
`

const StyledOptionHeader = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${uiTokens.spacing.gap.small}px;
  width: 100%;
`

const renderOption = ({ icon: Icon, title, description, onClick }: OptionDefinition, _index: number) => (
  <StyledOptionCard onClick={onClick}>
    <StyledOptionIconWrap>
      <Icon fontSize="medium" />
    </StyledOptionIconWrap>
    <StyledOptionBody>
      <StyledOptionHeader>
        <StyledOptionTitle title={title} subheader={description} />
      </StyledOptionHeader>
    </StyledOptionBody>
  </StyledOptionCard>
)

export function SetupFaceRecoPopup(props: ModalStepProps) {
  const { goToStep } = useWizard()
  const t = useTranslation()
  const { generateCode, invalidateCode } = useCodeContext()
  const dialogTitle = t('LBL_NGID_FACE_RECO_TITLE', 'Face recognition')
  const dialogMessage = t('LBL_NGID_FACE_RECO_WAYS', 'Choose preferred way of face recognition activation.')
  const handleSetupAtMachine = async () => {
    await generateCode('face')
    invalidateCode('nfc')
    sendNotification({
      message: t('LBL_NGID_FACE_RECO_NOTIFIC_MSG', 'Finish the activation process at the dialysis device.'),
      status: 'success',
      title: t('LBL_NGID_FACE_RECO_NOTIFIC_TITLE', 'Face recognition activation code created'),
    })
    goToStep(7)
  }
  const options: OptionDefinition[] = [
    {
      icon: PersonOutlined,
      title: t('LBL_NGID_FACE_RECO_WAY1_TITLE', 'Upload a photo'),
      description: t('LBL_NGID_FACE_RECO_WAY1_DESCR', 'Setup face recognition by uploading your photo.'),
      onClick: () => goToStep(6),
    },
    {
      icon: MonitorOutlined,
      title: t('LBL_NGID_FACE_RECO_WAY2_TITLE', 'Setup at the machine'),
      description: t(
        'LBL_NGID_FACE_RECO_WAY2_DESCR',
        'Get an activation code and finish the setup at the dialysis machine.',
      ),
      onClick: () => handleSetupAtMachine(),
    },
  ]
  const skipButton = t('LBL_NGID_BACK_BTN', 'Back')

  return (
    <>
      <HeaderPopup title={dialogTitle} modalRef={props.modalRef} />
      <StyledContent>
        <StyledMessageTypography variant="body1">{dialogMessage}</StyledMessageTypography>
        {options.map(renderOption)}
      </StyledContent>
      <FooterPopup secondary={{ label: skipButton, onClick: () => goToStep(3) }} />
    </>
  )
}
